// MyAttendance.js
import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';

export default function MyAttendance() {
  const [records, setRecords] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());

  useEffect(() => {
    api.get('/attendance/my-history', { params: { month, year } }).then(r => setRecords(r.data));
  }, [month, year]);

  const totalHours = records.reduce((acc, r) => acc + (r.totalMinutes || 0), 0) / 60;
  const presentDays = records.filter(r => ['present', 'late'].includes(r.status)).length;
  const lateDays = records.filter(r => r.isLate).length;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Mes Présences</h1>
        <div className="flex gap-3">
          <select value={month} onChange={e => setMonth(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            {Array.from({length: 12}, (_, i) => (
              <option key={i+1} value={i+1}>{new Date(2024, i).toLocaleString('fr', {month: 'long'})}</option>
            ))}
          </select>
          <select value={year} onChange={e => setYear(e.target.value)} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none">
            {[2024, 2025, 2026].map(y => <option key={y}>{y}</option>)}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 rounded-2xl p-4 text-center">
          <CheckCircle className="w-6 h-6 text-green-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-800">{presentDays}</p>
          <p className="text-gray-500 text-sm">Jours présents</p>
        </div>
        <div className="bg-yellow-50 rounded-2xl p-4 text-center">
          <AlertCircle className="w-6 h-6 text-yellow-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-800">{lateDays}</p>
          <p className="text-gray-500 text-sm">Retards</p>
        </div>
        <div className="bg-blue-50 rounded-2xl p-4 text-center">
          <Clock className="w-6 h-6 text-blue-500 mx-auto mb-2" />
          <p className="text-2xl font-bold text-gray-800">{totalHours.toFixed(1)}h</p>
          <p className="text-gray-500 text-sm">Total heures</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              {['Date', 'Entrée', 'Sortie', 'Durée', 'Statut'].map(h => (
                <th key={h} className="text-left px-6 py-3 text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {records.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-12 text-center text-gray-400">Aucun enregistrement</td></tr>
            ) : records.map(rec => (
              <tr key={rec.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 text-sm text-gray-700">
                  {format(new Date(rec.date), 'EEEE dd/MM', { locale: fr })}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {rec.clockIn ? format(new Date(rec.clockIn), 'HH:mm') : '-'}
                  {rec.isLate && <span className="ml-1 text-orange-500 text-xs">+{rec.lateMinutes}min</span>}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {rec.clockOut ? format(new Date(rec.clockOut), 'HH:mm') : '-'}
                </td>
                <td className="px-6 py-4 text-sm text-gray-700">
                  {rec.totalMinutes ? `${(rec.totalMinutes / 60).toFixed(1)}h` : '-'}
                  {rec.overtimeMinutes > 0 && <span className="ml-1 text-blue-500 text-xs">+{(rec.overtimeMinutes/60).toFixed(1)}h sup</span>}
                </td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    rec.status === 'present' ? 'bg-green-100 text-green-700' :
                    rec.status === 'late' ? 'bg-yellow-100 text-yellow-700' :
                    rec.status === 'absent' ? 'bg-red-100 text-red-600' :
                    'bg-gray-100 text-gray-600'
                  }`}>
                    {rec.status === 'present' ? 'Présent' : rec.status === 'late' ? 'En retard' : rec.status === 'absent' ? 'Absent' : rec.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
